import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import type { JsonPatchOperation } from '@/shared/types/common';

type RelatedTerm = {
  id: string;
  type: string;
  name: string;
  fullyQualifiedName: string;
  displayName: string;
  deleted: boolean;
};

export const updateGlossaryTermRelatedTerms = async (
  termId: string,
  newRelatedTermIds: string[],
  allTerms: Array<{ id: string; fullyQualifiedName: string; displayName?: string; name: string }>,
  currentRelatedTerms: RelatedTerm[]
): Promise<void> => {
  try {
    if (!termId) {
      throw new Error('Glossary term ID is required');
    }

    const operations: JsonPatchOperation[] = [];

    const currentSet = new Set(currentRelatedTerms.map((t) => t.id));
    const newSet = new Set(newRelatedTermIds);

    const toRemove = currentRelatedTerms.filter((t) => !newSet.has(t.id));
    const toAdd = newRelatedTermIds.filter((id) => !currentSet.has(id));

    toRemove.forEach((term) => {
      const index = currentRelatedTerms.findIndex((t) => t.id === term.id);
      if (index !== -1) {
        operations.push({
          op: 'remove',
          path: `/relatedTerms/${index}`,
        });
      }
    });

    if (toRemove.length > 0 && toAdd.length > 0) {
      const newRelatedTerms = newRelatedTermIds
        .map((id) => {
          const term = allTerms.find((t) => t.id === id);
          if (term) {
            return {
              id: term.id,
              type: 'glossaryTerm',
              name: term.name,
              fullyQualifiedName: term.fullyQualifiedName,
              displayName: term.displayName || term.name,
              deleted: false,
            };
          }
          return null;
        })
        .filter((t): t is NonNullable<typeof t> => t !== null);

      operations.push({
        op: 'replace',
        path: '/relatedTerms',
        value: newRelatedTerms,
      });
    } else if (toRemove.length === 0 && toAdd.length > 0) {
      toAdd.forEach((id) => {
        const term = allTerms.find((t) => t.id === id);
        if (term) {
          operations.push({
            op: 'add',
            path: `/relatedTerms/${currentRelatedTerms.length + operations.filter((op) => op.op === 'add').length}`,
            value: {
              id: term.id,
              type: 'glossaryTerm',
              name: term.name,
              fullyQualifiedName: term.fullyQualifiedName,
              displayName: term.displayName || term.name,
              deleted: false,
            },
          });
        }
      });
    }

    if (operations.length > 0) {
      await openMetadataApiClient.patch(`/glossaryTerms/${encodeURIComponent(termId)}`, operations, {
        headers: {
          'Content-Type': 'application/json-patch+json',
        },
      });
    }
  } catch (error) {
    console.error('Failed to update glossary term related terms:', error);
    throw error;
  }
};
